type Task<Sdk> = (sdk: Sdk) => void;

interface ILazySdk<Sdk> {
  /** Runs now when the SDK is loaded, otherwise once it is (oldest first, at most `limit` kept). */
  run: (task: Task<Sdk>) => void;
  /** The SDK is loaded: replays what was waiting. */
  resolve: (sdk: Sdk) => void;
  /** The SDK will never load (blocked chunk): forgets what was waiting. */
  abandon: () => void;
}

/**
 * A monitoring SDK that loads after first paint still has to hear about errors thrown before it is
 * ready. Calls made in that window wait here and replay in order.
 */
export const createLazySdk = <Sdk>(limit: number): ILazySdk<Sdk> => {
  let sdk: Sdk | undefined;
  let waiting: Task<Sdk>[] = [];

  return {
    run: task => {
      if (sdk) {
        task(sdk);
      } else if (waiting.length < limit) {
        waiting.push(task);
      }
    },
    resolve: loaded => {
      sdk = loaded;
      const replay = waiting;
      waiting = [];
      replay.forEach(task => task(loaded));
    },
    abandon: () => {
      waiting = [];
    },
  };
};

/**
 * Queues the page's uncaught errors and unhandled rejections for `report` until `stop()`. The SDK
 * installs its own handlers once it is initialised, so ours must go then or every later error is sent twice.
 */
export const bufferGlobalErrors = (
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>,
  report: (error: unknown, type: 'onerror' | 'onunhandledrejection') => void,
) => {
  const handleError = (event: Event) => {
    const { error, message } = event as ErrorEvent;
    report(error ?? new Error(message), 'onerror');
  };
  const handleRejection = (event: Event) =>
    report((event as PromiseRejectionEvent).reason, 'onunhandledrejection');

  target.addEventListener('error', handleError);
  target.addEventListener('unhandledrejection', handleRejection);

  return () => {
    target.removeEventListener('error', handleError);
    target.removeEventListener('unhandledrejection', handleRejection);
  };
};
